import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Download, RefreshCw, ExternalLink, CheckCircle, AlertCircle, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KuotaSyncManagement = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [lastUpdate, setLastUpdate] = useState('');
  const [selectedPkg, setSelectedPkg] = useState(null);

  const fetchKuota = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/kuota/`);
      setPackages(res.data.packages);
      setLastUpdate(res.data.last_update);
      setSyncResult(null);
      toast.success(`${res.data.total} paket dimuat dari UmrahBISA`);
    } catch (err) {
      toast.error('Gagal memuat data kuota');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (packages.length === 0) {
      toast.error('Muat data kuota terlebih dahulu');
      return;
    }
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/kuota/sync`);
      setSyncResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal sinkronisasi');
    } finally {
      setSyncing(false);
    }
  };

  const formatRupiah = (num) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);
  const formatDate = (d) => {
    if (!d || d.includes('NaN')) return '-';
    try {
      return new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return '-'; }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Tarik Data Paket</h1>
          <p className="text-muted-foreground text-sm mt-1">Sinkronisasi paket Umrah dari UmrahBISA ke database lokal</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchKuota} disabled={loading} variant="outline">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Muat Data
          </Button>
          <Button onClick={handleSync} disabled={syncing || packages.length === 0}>
            {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Sinkronkan
          </Button>
        </div>
      </div>

      {lastUpdate && (
        <p className="text-xs text-muted-foreground mb-4">Terakhir diperbarui UmrahBISA: {lastUpdate}</p>
      )}

      {syncResult && (
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="font-semibold text-green-800">{syncResult.message}</span>
            </div>
            <div className="flex gap-4 text-sm text-green-700">
              <span>Baru: {syncResult.created}</span>
              <span>Diperbarui: {syncResult.updated}</span>
              <span>Dilewati: {syncResult.skipped}</span>
              {syncResult.errors?.length > 0 && <span className="text-red-600">Error: {syncResult.errors.length}</span>}
            </div>
          </CardContent>
        </Card>
      )}

      {packages.length === 0 && !loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <Download className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Klik "Muat Data" untuk mengambil data kuota dari UmrahBISA</p>
          </CardContent>
        </Card>
      )}

      {packages.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-[600px]">
              <table className="w-full">
                <thead className="sticky top-0 bg-background">
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold text-sm">Nama Paket</th>
                    <th className="text-center p-3 font-semibold text-sm">Durasi</th>
                    <th className="text-center p-3 font-semibold text-sm">Tersisa</th>
                    <th className="text-center p-3 font-semibold text-sm">Kuota</th>
                    <th className="text-left p-3 font-semibold text-sm">Maskapai</th>
                    <th className="text-left p-3 font-semibold text-sm">Harga</th>
                    <th className="text-center p-3 font-semibold text-sm">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((p, i) => (
                    <tr key={i} className="border-b hover:bg-muted/50">
                      <td className="p-3 text-sm">{p.name}</td>
                      <td className="p-3 text-sm text-center">{p.duration_days}d</td>
                      <td className="p-3 text-sm text-center">
                        <Badge variant={p.remaining > 0 ? 'default' : 'destructive'}>
                          {p.remaining > 0 ? p.remaining : 'FULL'}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm text-center">{p.quota}</td>
                      <td className="p-3 text-sm">{p.airline}</td>
                      <td className="p-3 text-sm">{formatRupiah(p.price)}</td>
                      <td className="p-3 text-sm text-center">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedPkg(p)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail Modal */}
      {selectedPkg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedPkg(null)}>
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] overflow-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-bold">{selectedPkg.name}</h3>
              <button onClick={() => setSelectedPkg(null)} className="text-muted-foreground hover:text-foreground">&times;</button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <span className="text-muted-foreground">Durasi:</span><span>{selectedPkg.duration_days} hari</span>
                <span className="text-muted-foreground">Maskapai:</span><span>{selectedPkg.airline}</span>
                <span className="text-muted-foreground">Rute:</span><span>{selectedPkg.route || '-'}</span>
                <span className="text-muted-foreground">Harga:</span><span>{formatRupiah(selectedPkg.price)}</span>
                <span className="text-muted-foreground">Kuota:</span><span>{selectedPkg.quota} (tersisa {selectedPkg.remaining})</span>
                <span className="text-muted-foreground">Berangkat:</span><span>{formatDate(selectedPkg.departure)}</span>
                <span className="text-muted-foreground">Pulang:</span><span>{formatDate(selectedPkg.return)}</span>
              </div>
              <div className="border-t pt-3">
                <p className="text-muted-foreground">Hotel Mekkah: {selectedPkg.mekkah_hotel || '-'}</p>
                <p className="text-muted-foreground">Hotel Madinah: {selectedPkg.madinah_hotel || '-'}</p>
              </div>
              <div className="border-t pt-3 flex gap-2">
                {selectedPkg.brochure_url && (
                  <a href={selectedPkg.brochure_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-primary hover:underline">
                    <ExternalLink className="h-3 w-3 mr-1" /> Brosur
                  </a>
                )}
                {selectedPkg.itinerary_url && (
                  <a href={selectedPkg.itinerary_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-primary hover:underline">
                    <ExternalLink className="h-3 w-3 mr-1" /> Itinerary
                  </a>
                )}
                {selectedPkg.group_link && (
                  <a href={selectedPkg.group_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-primary hover:underline">
                    <ExternalLink className="h-3 w-3 mr-1" /> Grup WA
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KuotaSyncManagement;
